/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React, { useContext, useEffect, useState } from "react";
import { Button } from "baseui/button";
import { StatefulTooltip } from "baseui/tooltip";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import { AxiosContext } from "../../Utils/AuthContext";
import { NodeDescriptor, TransformScope } from "Api";
import { FaInfoCircle } from "react-icons/fa";
import { TransformDetailsTooltipContent } from "../../Shared/TransformDetailsTooltipContent";
import { Endpoints } from "../../Utils/Common";
import { getTooltipOverrides } from "../../DesignSystem/TooltipStyles";

interface SidePanelProps {
  show: boolean;
  panelWidth?: number;
}

export const DEFAULT_TRANSFORM_PANEL_WIDTH = 240;

const TransformSidePanel: React.FC<SidePanelProps> = ({
  show,
  panelWidth = DEFAULT_TRANSFORM_PANEL_WIDTH,
}): JSX.Element => {
  const axiosContext = useContext(AxiosContext);
  const { currentTheme } = useDexterTheme();

  const [transformDescriptors, setTransformDescriptors] = useState<
    NodeDescriptor[]
  >([]);

  useEffect(() => {
    const fetchDescriptors = async (): Promise<void> => {
      const response = await axiosContext.get(
        Endpoints.GetTransformNodeDescriptors
      );

      if (response.status === 200) {
        setTransformDescriptors(response.data);
      }
    };
    fetchDescriptors();
  }, [axiosContext]);

  const getSubtitle = (descriptor: NodeDescriptor): string | null => {
    if (!descriptor.transformScope) return null;

    switch (descriptor.transformScope) {
      case TransformScope.GRANULAR:
        return "granular";
      case TransformScope.FULL:
        return "full file";
      default:
        return null;
    }
  };

  const SIDE_PANEL_STYLE: React.CSSProperties = {
    width: panelWidth,
    background: currentTheme.colors.background,
    boxShadow: "2px 0 8px rgba(0,0,0,0.1)",
    minWidth: 180,
    pointerEvents: "auto",
    transition: "transform 0.4s cubic-bezier(0.4,0,0.2,1)",
    transform: show ? "translateX(0)" : `translateX(-${panelWidth}px)`,
    display: "flex",
    flexDirection: "column",
    padding: "16px",
    borderRight: `2px solid ${currentTheme.colors.background}`,
    position: "relative",
  };

  const LIST_STYLE: React.CSSProperties = {
    flex: 1,
    overflowY: "auto",
    padding: 0,
    margin: 0,
    overflow: "visible",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  };

  const ITEM_STYLE: React.CSSProperties = {
    width: "100%",
    background: currentTheme.colors.dropdownBackground,
    border: `1px solid ${currentTheme.colors.primaryLight}`,
    borderRadius: 8,
    padding: "12px 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
    cursor: "grab",
    position: "relative",
    minHeight: 54,
    userSelect: "none",
    boxSizing: "border-box",
  };

  const handleDragStart = (
    event: React.DragEvent,
    descriptor: NodeDescriptor
  ): void => {
    event.dataTransfer.setData(
      "application/transformnode",
      JSON.stringify(descriptor)
    );
    event.dataTransfer.effectAllowed = "move";
  };

  return (
    <div style={SIDE_PANEL_STYLE}>
      <ul style={LIST_STYLE}>
        {transformDescriptors.map((descriptor) => {
          const subtitle = getSubtitle(descriptor);

          return (
            <li
              key={descriptor.id}
              style={{
                ...ITEM_STYLE,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 54,
                position: "relative",
                width: "100%",
                overflow: "visible",
              }}
              draggable
              onDragStart={(e) => handleDragStart(e, descriptor)}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  maxWidth: "70%",
                }}
              >
                <span
                  style={{
                    fontFamily: currentTheme.fonts.primary,
                    fontSize: currentTheme.fontSizes.medium,
                    fontWeight: 600,
                    color: currentTheme.colors.text,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={descriptor.displayName}
                >
                  {descriptor.displayName}
                </span>
                {subtitle && (
                  <span
                    style={{
                      fontFamily: currentTheme.fonts.primary,
                      fontSize: "1.0 rem",
                      color: currentTheme.colors.secondaryTextColor,
                      opacity: 0.7,
                      marginTop: "2px",
                    }}
                  >
                    {subtitle}
                  </span>
                )}
              </div>
              <div>
                <StatefulTooltip
                  content={
                    <TransformDetailsTooltipContent descriptor={descriptor} />
                  }
                  showArrow
                  placement="right"
                  focusLock={false}
                  autoFocus={false}
                  returnFocus={false}
                  overrides={getTooltipOverrides(currentTheme)}
                >
                  <Button
                    kind="secondary"
                    size="mini"
                    overrides={{
                      BaseButton: {
                        style: {
                          padding: "0 6px",
                          fontSize: 14,
                          minWidth: 0,
                          height: 32,
                          marginLeft: 10,
                          backgroundColor: "transparent",
                          color: currentTheme.colors.editButtonColor,
                          cursor: "pointer",
                          ":hover": {
                            backgroundColor:
                              currentTheme.colors
                                .editButtonHoverBackgroundColor,
                          },
                        },
                      },
                    }}
                  >
                    <FaInfoCircle size={18} />
                  </Button>
                </StatefulTooltip>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default TransformSidePanel;
